import { BigNumber } from 'bignumber.js';
import {
    ARRAY_SIZE_OF_ONE_ELEMENT,
    ARRAY_SIZE_OF_TWO_ELEMENTS,
    FIRST_ELEMENT_INDEX,
    MULTIPLIER_FOR_PERCENTAGE,
    THOUSANDS_SEPARATOR,
    TWO_DECIMAL_PLACES,
} from '../constants/constants';

export class NumberUtils {
    public static sumNumbers(numbers: number[] | BigNumber[], returnBigNumber?: false, decimalPlaces?: number): number;
    public static sumNumbers(numbers: number[] | BigNumber[], returnBigNumber: true, decimalPlaces?: number): BigNumber;
    public static sumNumbers(
        numbers: number[] | BigNumber[],
        returnBigNumber = false,
        decimalPlaces = TWO_DECIMAL_PLACES,
    ): number | BigNumber {
        if (numbers.length < ARRAY_SIZE_OF_TWO_ELEMENTS) {
            throw new Error('There should be at least two numbers to sum');
        }

        let sum = new BigNumber(0);

        for (const num of numbers) {
            const roundedNumber = new BigNumber(num).decimalPlaces(decimalPlaces);
            sum = sum.plus(roundedNumber);
        }

        return returnBigNumber ? sum : sum.toNumber();
    }

    public static subtractNumbers(
        minuend: number | BigNumber,
        subtrahend: number | BigNumber,
        returnBigNumber?: false,
        decimalPlaces?: number,
    ): number;
    public static subtractNumbers(
        minuend: number | BigNumber,
        subtrahend: number | BigNumber,
        returnBigNumber: true,
        decimalPlaces?: number,
    ): BigNumber;
    public static subtractNumbers(
        minuend: number | BigNumber,
        subtrahend: number | BigNumber,
        returnBigNumber = false,
        decimalPlaces = TWO_DECIMAL_PLACES,
    ): number | BigNumber {
        if (!NumberUtils.isFinite(minuend) || !NumberUtils.isFinite(subtrahend)) {
            throw new Error("Can't subtract numbers that are not finite");
        }

        const minuendBigNumber = new BigNumber(minuend).decimalPlaces(decimalPlaces);

        const subtrahendBigNumber = new BigNumber(subtrahend).decimalPlaces(decimalPlaces);

        const difference = minuendBigNumber.minus(subtrahendBigNumber);

        return returnBigNumber ? difference : difference?.toNumber();
    }

    public static multiplyNumbers(
        numbers: number[] | BigNumber[],
        returnBigNumber?: false,
        decimalPlaces?: number,
    ): number;
    public static multiplyNumbers(
        numbers: number[] | BigNumber[],
        returnBigNumber: true,
        decimalPlaces?: number,
    ): BigNumber;
    public static multiplyNumbers(
        numbers: number[] | BigNumber[],
        returnBigNumber = false,
        decimalPlaces = TWO_DECIMAL_PLACES,
    ): number | BigNumber {
        if (numbers.length < ARRAY_SIZE_OF_TWO_ELEMENTS) {
            throw new Error('There should be at least two numbers to multiply');
        }
        let multiplication: BigNumber = new BigNumber(numbers[FIRST_ELEMENT_INDEX]);

        for (let numberIndex = FIRST_ELEMENT_INDEX + 1; numberIndex < numbers.length; numberIndex++) {
            const multiplier = new BigNumber(numbers[numberIndex]);
            multiplication = multiplication.multipliedBy(multiplier);
        }
        multiplication = multiplication.decimalPlaces(decimalPlaces);
        return returnBigNumber ? multiplication : multiplication.toNumber();
    }

    public static calculatePercentage(
        value: number | BigNumber,
        totalValue: number | BigNumber,
        returnBigNumber?: false,
        decimalPlaces?: number,
    ): number;
    public static calculatePercentage(
        value: number | BigNumber,
        totalValue: number | BigNumber,
        returnBigNumber: true,
        decimalPlaces?: number,
    ): BigNumber;
    public static calculatePercentage(
        value: number | BigNumber,
        totalValue: number | BigNumber,
        returnBigNumber = false,
        decimalPlaces = TWO_DECIMAL_PLACES,
    ): number | BigNumber {
        const dividend = new BigNumber(value);
        const dividendInPercent = NumberUtils.multiplyNumbers(
            [dividend.toNumber(), MULTIPLIER_FOR_PERCENTAGE],
            true,
            decimalPlaces,
        );

        const divisor = new BigNumber(totalValue);

        let percentage = dividendInPercent.div(divisor);
        percentage = percentage.decimalPlaces(decimalPlaces);
        return returnBigNumber ? percentage : percentage.toNumber();
    }

    public static removeThousandSeparator(
        value: string,
        defaultValue: number,
        returnBigNumber?: false,
        decimalPlaces?: number,
    ): number;
    public static removeThousandSeparator(
        value: string,
        defaultValue: number | BigNumber,
        returnBigNumber: true,
        decimalPlaces?: number,
    ): BigNumber;
    public static removeThousandSeparator(
        value: string,
        defaultValue: number | BigNumber,
        returnBigNumber = false,
        decimalPlaces = TWO_DECIMAL_PLACES,
    ): number | BigNumber {
        const allThousandsSeparatorRegex = new RegExp(THOUSANDS_SEPARATOR, 'g');
        const valueWithoutThousandSeparator = value.replace(allThousandsSeparatorRegex, '');
        const bigNumber = new BigNumber(valueWithoutThousandSeparator);

        if (!NumberUtils.isFinite(bigNumber)) {
            const roundedDefaultValue = NumberUtils.roundNumber(defaultValue, true);
            return returnBigNumber ? roundedDefaultValue : roundedDefaultValue.toNumber();
        }
        const roundedBigNumber = NumberUtils.roundNumber(bigNumber, true, decimalPlaces);

        return returnBigNumber ? roundedBigNumber : roundedBigNumber.toNumber();
    }

    /** FX (REV-21512): THE rate display precision — 5 decimals, '' when missing. One place only. */
    public static formatFxRate(rate: number | null | undefined): string {
        return rate != null ? rate.toFixed(5) : '';
    }

    public static roundNumber(
        num: number | string | BigNumber,
        returnBigNumber?: false,
        decimalPlaces?: number,
        roundingMode?: BigNumber.RoundingMode,
    ): number;
    public static roundNumber(
        num: number | string | BigNumber,
        returnBigNumber: true,
        decimalPlaces?: number,
        roundingMode?: BigNumber.RoundingMode,
    ): BigNumber;
    public static roundNumber(
        num: number | string | BigNumber,
        returnBigNumber = false,
        decimalPlaces = TWO_DECIMAL_PLACES,
        roundingMode?: BigNumber.RoundingMode,
    ): number | BigNumber {
        if (num?.toString().length > 0 && !NumberUtils.isFinite(num)) {
            throw new Error('The passed value is not a finite  number');
        }

        const bigNumber = new BigNumber(num).decimalPlaces(decimalPlaces, roundingMode);
        return returnBigNumber ? bigNumber : bigNumber.toNumber();
    }

    public static areNumbersEqual(num1: number | BigNumber, num2: number | BigNumber): boolean {
        const roundedNumber1 = new BigNumber(num1);

        const roundedNumber2 = new BigNumber(num2);
        return roundedNumber1.isEqualTo(roundedNumber2);
    }

    public static getIntegerValue(value: string | number | BigNumber): number {
        return new BigNumber(value).integerValue().toNumber();
    }

    public static isFinite(value: string | number | BigNumber): boolean {
        return new BigNumber(value).isFinite();
    }

    public static isZero(value: number | BigNumber): boolean {
        return new BigNumber(value).isZero();
    }

    public static filterNonNumber(
        numbers: (number | null | undefined)[] | (BigNumber | null | undefined)[],
        returnBigNumber?: false,
    ): number[];
    public static filterNonNumber(
        numbers: (number | null | undefined)[] | (BigNumber | null | undefined)[],
        returnBigNumber: true,
    ): BigNumber[];
    public static filterNonNumber(
        numbers: (number | null | undefined)[] | (BigNumber | null | undefined)[],
        returnBigNumber = false,
    ): number[] | BigNumber[] {
        const filteredBigNumber: BigNumber[] = [];

        numbers.forEach((num: number | BigNumber | null | undefined) => {
            if (num == null) {
                return;
            }

            const bigNum = new BigNumber(num);
            if (!bigNum.isFinite()) {
                return;
            }

            filteredBigNumber.push(bigNum);
        });

        return returnBigNumber ? filteredBigNumber : filteredBigNumber.map((num) => num.toNumber());
    }

    public static sumIgnoringInfiniteNumbers(
        numbers: Array<number | null | undefined> | Array<BigNumber | null | undefined>,
        defaultValue: undefined,
        returnBigNumber?: false,
        decimalPlaces?: number,
    ): number | undefined;
    public static sumIgnoringInfiniteNumbers(
        numbers: Array<number | null | undefined> | Array<BigNumber | null | undefined>,
        defaultValue: number,
        returnBigNumber?: false,
        decimalPlaces?: number,
    ): number;
    public static sumIgnoringInfiniteNumbers(
        numbers: Array<number | null | undefined> | Array<BigNumber | null | undefined>,
        defaultValue: undefined,
        returnBigNumber: true,
        decimalPlaces?: number,
    ): BigNumber | undefined;
    public static sumIgnoringInfiniteNumbers(
        numbers: Array<number | null | undefined> | Array<BigNumber | null | undefined>,
        defaultValue: BigNumber | number,
        returnBigNumber: true,
        decimalPlaces?: number,
    ): BigNumber;
    public static sumIgnoringInfiniteNumbers(
        numbers: Array<number | null | undefined> | Array<BigNumber | null | undefined>,
        defaultValue: number | BigNumber | undefined,
        returnBigNumber = false,
        decimalPlaces = TWO_DECIMAL_PLACES,
    ): number | BigNumber | undefined {
        let sumValue: BigNumber;

        const filteredValuesToBeSummed = NumberUtils.filterNonNumber(numbers, true);

        if (filteredValuesToBeSummed.length >= ARRAY_SIZE_OF_TWO_ELEMENTS) {
            sumValue = NumberUtils.sumNumbers(filteredValuesToBeSummed, true, decimalPlaces);
        } else if (filteredValuesToBeSummed.length === ARRAY_SIZE_OF_ONE_ELEMENT) {
            sumValue = NumberUtils.roundNumber(filteredValuesToBeSummed[FIRST_ELEMENT_INDEX], true, decimalPlaces);
        } else {
            return defaultValue;
        }

        return returnBigNumber ? sumValue : sumValue.toNumber();
    }
}
